import { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { saveNeedsFromNotes } from '../../utils/needsService';
import { FinanceReport } from '../../types/financeReport';
import { FormInput } from '../ui/FormInput';
import { MeetingTypeSelect } from '../ui/MeetingTypeSelect';
import { WorshipReportSelect } from '../ui/WorshipReportSelect';
import { formatNumber } from '../../utils/format';
import { X } from 'lucide-react';
import { WorshipReport } from '../../types/worshipReport';
import { toast } from 'sonner';

interface EditFinanceReportFormProps {
  report: FinanceReport;
  onClose: () => void;
  onUpdate?: () => void;
}

export default function EditFinanceReportForm({ report, onClose, onUpdate }: EditFinanceReportFormProps) {
  const [formData, setFormData] = useState<FinanceReport>({
    ...report,
    finances: { ...report.finances }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-populate date and meeting type from selected worship report
  useEffect(() => {
    const fetchWorshipReportData = async () => {
      if (!formData.worshipReportId) {
        return;
      }

      try {
        const worshipReportRef = doc(db, 'worshipReports', formData.worshipReportId);
        const worshipReportSnap = await getDoc(worshipReportRef);
        
        if (worshipReportSnap.exists()) {
          const worshipReportData = worshipReportSnap.data() as WorshipReport;
          setFormData(prev => ({
            ...prev,
            date: worshipReportData.date,
            meetingTypeId: worshipReportData.meetingTypeId
          }));
        }
      } catch (error) {
        console.error('Error fetching worship report:', error);
      }
    };

    fetchWorshipReportData();
  }, [formData.worshipReportId]);

  const calculateTotal = () => {
    return formData.finances.tithes + formData.finances.regularOfferings + formData.finances.specialOfferings;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (!report.id) {
        throw new Error('ID du rapport manquant');
      }

      const totalFinances = calculateTotal();
      
      const reportRef = doc(db, 'financeReports', report.id);
      await updateDoc(reportRef, {
        ...formData,
        totalFinances,
        updatedAt: new Date()
      });

      // Sauvegarder les besoins si présents et modifiés
      if (formData.needsNotes && formData.needsNotes !== report.needsNotes) {
        await saveNeedsFromNotes({
          reportId: report.id,
          reportType: 'finance',
          department: 'Finance',
          needsNotes: formData.needsNotes,
          reportDate: formData.date
        });
      }

      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Error updating finance report:', error);
      toast.error('Erreur lors de la mise à jour du rapport financier');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinancesChange = (field: keyof FinanceReport['finances'], value: string) => {
    const numValue = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      finances: {
        ...prev.finances,
        [field]: numValue
      }
    }));
  };

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Modifier le rapport financier</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informations générales */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-6">Informations générales</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                label="Date du culte"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                required
              />
              <MeetingTypeSelect
                value={formData.meetingTypeId}
                onChange={(value) => setFormData(prev => ({ ...prev, meetingTypeId: value }))}
               required={false}
               disabled={true}
              />
            </div>
          </div>

          {/* Rapport financier */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-6">Rapport financier</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FormInput
                type="number"
                min="0"
                label="Dîmes"
                value={formData.finances.tithes}
                onChange={(e) => handleFinancesChange('tithes', e.target.value)}
              />
              <FormInput
                type="number"
                min="0"
                label="Offrandes ordinaires"
                value={formData.finances.regularOfferings}
                onChange={(e) => handleFinancesChange('regularOfferings', e.target.value)}
              />
              <FormInput
                type="number"
                min="0"
                label="Offrandes spéciales"
                value={formData.finances.specialOfferings}
                onChange={(e) => handleFinancesChange('specialOfferings', e.target.value)}
              />
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-600">
                Total des entrées: <span className="text-lg font-bold text-[#00665C]">{formatNumber(calculateTotal())} FCFA</span>
              </p>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Notes / Observations
            <div className="md:col-span-2">
              <WorshipReportSelect
                value={formData.worshipReportId || ''}
                onChange={(value) => setFormData(prev => ({ ...prev, worshipReportId: value }))}
                required
              departmentCollection="financeReports"
              />
            </div>
            </label>
            <textarea
              placeholder="Observations financières..."
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
              value={formData.notes || ''}
              required={false}
              disabled={!!formData.worshipReportId}
            />
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Mise à jour...' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>
    </div>

    </>
  );
}